import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '@/services/api'
import { formatRelativeTime } from '@/utils/format-date'
import type { NotificationResponse } from '@/services/api'

const SEVERITY_COLORS: Record<string, string> = {
  info: 'text-blue-500',
  warning: 'text-amber-500',
  critical: 'text-danger-600',
}

const REFETCH_INTERVAL_MS = 60_000

export function NotificationBell() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { data: unreadData } = useQuery({
    queryKey: ['admin-notification-unread-count'],
    queryFn: () => api.adminGetUnreadCount(),
    refetchInterval: REFETCH_INTERVAL_MS,
  })

  const { data: recentData } = useQuery({
    queryKey: ['admin-notifications-recent'],
    queryFn: () => api.adminListNotifications({ limit: 5 }),
    enabled: open,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => api.adminMarkNotificationRead(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-notification-unread-count'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-notifications-recent'] })
    },
  })

  const unreadCount = unreadData?.count ?? 0

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => { document.removeEventListener('mousedown', handleClickOutside) }
  }, [open])

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setOpen(!open) }}
        className="relative p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        aria-label="Notifications"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-danger-600 rounded-full min-w-[18px]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {(recentData?.data ?? []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No notifications</p>
            ) : (
              (recentData?.data ?? []).map((n: NotificationResponse) => (
                <button
                  key={n.id}
                  onClick={() => {
                    if (!n.isRead) markRead.mutate(n.id)
                  }}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 ${
                    n.isRead ? '' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <SeverityIcon severity={n.severity} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${n.isRead ? 'text-gray-700' : 'font-medium text-gray-900'}`}>
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{n.message}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatRelativeTime(n.createdAt)}</p>
                    </div>
                    {!n.isRead && (
                      <span className="inline-block w-2 h-2 bg-primary-500 rounded-full mt-1.5 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2 border-t border-gray-100">
            <Link
              to="/admin/notifications"
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              onClick={() => { setOpen(false) }}
            >
              View all
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

function SeverityIcon({ severity }: { severity: string }) {
  const colorClass = SEVERITY_COLORS[severity] ?? 'text-gray-400'

  if (severity === 'critical') {
    return (
      <svg className={`w-5 h-5 flex-shrink-0 ${colorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    )
  }

  if (severity === 'warning') {
    return (
      <svg className={`w-5 h-5 flex-shrink-0 ${colorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  return (
    <svg className={`w-5 h-5 flex-shrink-0 ${colorClass}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

